import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useConsent } from "@/components/consent/ConsentProvider";

/**
 * Per-purpose consent modal. Mounted at the app root next to <CookieBanner />
 * and opened by either the banner's "Personnaliser" button or the footer's
 * "Mes choix cookies" link.
 */
export default function CookieBannerModal() {
  const { t } = useTranslation();
  const { state, isOpen, close, save } = useConsent();
  const [analytics, setAnalytics] = useState(state.analytics);
  const [ads, setAds] = useState(state.ads);
  const [personalization, setPersonalization] = useState(state.personalization);

  // Sync local switches with the latest committed state every time the modal
  // opens. Without this, reopening after a change shows the previous draft.
  useEffect(() => {
    if (isOpen) {
      setAnalytics(state.analytics);
      setAds(state.ads);
      setPersonalization(state.personalization);
    }
  }, [isOpen, state]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("legal.cookies.modal.title")}</DialogTitle>
          <DialogDescription>{t("legal.cookies.modal.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Essential — always on, disabled. */}
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <div className="space-y-1">
              <Label className="text-sm font-medium text-foreground">{t("legal.cookies.modal.essentials.title")}</Label>
              <p className="text-xs leading-5 text-muted-foreground">{t("legal.cookies.modal.essentials.description")}</p>
            </div>
            <Switch checked disabled aria-label={t("legal.cookies.modal.essentials.title")} />
          </div>

          {/* Analytics */}
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <div className="space-y-1">
              <Label htmlFor="consent-analytics" className="text-sm font-medium text-foreground">
                {t("legal.cookies.modal.analytics.title")}
              </Label>
              <p className="text-xs leading-5 text-muted-foreground">{t("legal.cookies.modal.analytics.description")}</p>
            </div>
            <Switch id="consent-analytics" checked={analytics} onCheckedChange={setAnalytics} />
          </div>

          {/* Ads */}
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <div className="space-y-1">
              <Label htmlFor="consent-ads" className="text-sm font-medium text-foreground">
                {t("legal.cookies.modal.ads.title")}
              </Label>
              <p className="text-xs leading-5 text-muted-foreground">{t("legal.cookies.modal.ads.description")}</p>
            </div>
            <Switch id="consent-ads" checked={ads} onCheckedChange={setAds} />
          </div>

          {/* Personalization */}
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <div className="space-y-1">
              <Label htmlFor="consent-personalization" className="text-sm font-medium text-foreground">
                {t("legal.cookies.modal.personalization.title")}
              </Label>
              <p className="text-xs leading-5 text-muted-foreground">{t("legal.cookies.modal.personalization.description")}</p>
            </div>
            <Switch id="consent-personalization" checked={personalization} onCheckedChange={setPersonalization} />
          </div>

          <p className="text-xs leading-5 text-muted-foreground">
            {t("legal.cookies.modal.learnMore")}{" "}
            <Link to="/cookies" className="text-primary hover:underline" onClick={close}>
              {t("legal.cookies.modal.cookieListLink")}
            </Link>{" "}
            ·{" "}
            <Link to="/politique-rgpd" className="text-primary hover:underline" onClick={close}>
              {t("legal.cookies.modal.privacyLink")}
            </Link>
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={close}>
            {t("legal.cookies.modal.cancel")}
          </Button>
          <Button onClick={() => save({ analytics, ads, personalization })}>
            {t("legal.cookies.modal.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
